import ScrollReveal from "@/components/base/ScrollReveal";
import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";

export default function Services() {
  const location = useLocation();

  useEffect(() => {
    const scrollTo = (location.state as { scrollTo?: string })?.scrollTo;
    if (scrollTo) {
      const el = document.getElementById(scrollTo);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location]);

  const services = [
    {
      icon: "ri-compass-3-line",
      title: "AI Strategy Development",
      short: "Clear AI strategy starts with understanding where the organization is today.",
      description:
        "Clear AI strategy starts with understanding where the organization is today, where it wants to go, and what barriers must be addressed. VantageSight helps leaders define practical AI aspirations, goals, priorities, roadmaps, and implementation plans that are grounded in organizational readiness and stakeholder input.",
      leadIn: "VantageSight guides, advises, and accelerates leaders and teams in efforts such as:",
      includes: [
        "Enterprise and organization-specific AI strategy development",
        "AI aspiration, goals, and objectives definition",
        "AI roadmap development",
        "Strategy implementation planning",
        "Stakeholder alignment",
        "Quality review of existing AI plans",
      ],
      ideal:
        "Organizations that need direction before investing in AI — companies, agencies, and enterprise teams ready to define their AI future with clarity.",
    },
    {
      icon: "ri-shield-check-line",
      title: "AI Governance",
      short: "Establish practical, proportionate AI governance so AI use can grow with confidence, consistency, and appropriate oversight.",
      description:
        "VantageSight helps organizations establish practical, proportionate AI governance so AI use can grow with confidence, consistency, and appropriate oversight and comply with relevant regulations and standards (such as U.S. OMB Memos, the EU AI Act, U.S. state laws, ISO/IEC 42001, etc.).",
      leadIn: "VantageSight\u2019s guidance accelerates leaders and teams to establish effective AI governance practices such as:",
      includes: [
        "Trustworthy AI principles aligned with relevant standards/directives",
        "AI use case inventory approach and use case reviews",
        "AI governance processes and review points aligned with the AI lifecycle",
        "Regulations, standards, and guidance gap analysis and compliance (international/federal/national laws, industry regulations, state/province laws, ISO/IEC standards, NIST and OECD guidance, etc.)",
        "AI use case risk management approach",
        "Clear governance roles, responsibilities, and ownership",
        "Internal AI policies",
      ],
      ideal:
        "Public sector and private sector organizations that need governance before AI adoption scales — or governance review for existing initiatives.",
    },
    {
      icon: "ri-team-line",
      title: "AI Workforce Readiness",
      short: "Assess workforce readiness, identify implications, and plan for the roles, knowledge, skills, and abilities needed for AI-enabled work.",
      description:
        "Successful AI adoption depends on leaders and employees being ready to work with AI in a clear, confident, and responsible way. VantageSight helps organizations assess workforce readiness, identify workforce implications, and plan for the roles, knowledge, skills, and abilities needed for AI-enabled work. VantageSight also provides clarity about what types of AI-related organizational structures (groups, teams, community of practice, center of excellence, etc.) may make the most sense for the enterprise.",
      leadIn: "VantageSight focuses and guides leaders and their teams in activities such as:",
      includes: [
        "Workforce AI readiness assessment",
        "Leader AI readiness considerations",
        "AI roles and skills planning",
        "Training strategy development",
        "Change readiness",
      ],
      ideal:
        "Organizations preparing for AI adoption that need to understand and address workforce readiness before change accelerates.",
    },
    {
      icon: "ri-rocket-line",
      title: "AI Adoption",
      short: "Create practical adoption approaches that reduce confusion and increase the likelihood of successful AI use.",
      description:
        "AI adoption is not just a technology challenge. It requires clear goals, stakeholder involvement, business alignment, communication, governance, and ongoing review. VantageSight helps organizations create practical adoption approaches that reduce confusion and increase the likelihood of successful AI use.",
      leadIn: "VantageSight advises organizations in activities such as:",
      includes: [
        "AI adoption planning",
        "Stakeholder engagement",
        "Adoption barriers and enablers identification and mitigation",
        "Roadmap creation and implementation",
        "Communication and alignment across the enterprise",
      ],
      ideal:
        "Organizations that have AI tools or pilots in place but need help driving real adoption across teams, departments, or the enterprise.",
    },
    {
      icon: "ri-slideshow-line",
      title: "Workshops & Assessments",
      short: "Help organizations quickly clarify where they are, what they need, and what steps to take next.",
      description:
        "VantageSight helps leaders and teams design effective workshops and assessments for organizations to quickly clarify where they are, what they need, and what steps to take next. VantageSight can also provide workshop facilitation support and assessment results analysis, if needed, on a targeted basis.",
      leadIn: "Examples of workshops and assessments with which VantageSight can help include:",
      includes: [
        "Organizational and workforce AI readiness assessments",
        "AI governance and compliance gap assessments",
        "AI strategy and governance workshops",
        "Executive briefings and targeted short sessions",
        "Independent quality reviews",
        "AI adoption planning sessions",
      ],
      ideal:
        "Organizations that need clarity on where they stand and what to do next before committing to larger advisory engagements.",
    },
  ];

  const schemaItems = services.map((s, i) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": s.title,
    "provider": {
      "@type": "ProfessionalService",
      "name": "VantageSight AI Advisory LLC",
      "url": "https://vantagesight.readdy.co/"
    },
    "description": s.description,
    "areaServed": {
      "@type": "Country",
      "name": "United States"
    }
  }));

  return (
    <main>
      {/* Schema.org Service JSON-LD */}
      {schemaItems.map((schema, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}

      {/* Hero with background */}
      <section id="advisory-services" className="relative bg-bg-section pt-28 pb-16 md:pt-36 md:pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20dark%20blue%20professional%20background%20with%20subtle%20circuit%20patterns%20and%20soft%20glowing%20connections%20modern%20technology%20elegant%20minimal%20corporate%20deep%20navy%20tones&width=1920&height=600&seq=services-hero-bg&orientation=landscape"
            alt=""
            className="w-full h-full object-cover opacity-25"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#07111F]/80 via-[#07111F]/90 to-[#07111F]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        <div className="absolute top-1/2 -right-32 w-[400px] h-[400px] bg-primary/5 rounded-full blur-[120px]" />

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <ScrollReveal>
            <div className="max-w-4xl mx-auto text-center">
              <span className="font-sans text-[14px] font-medium text-primary tracking-wider uppercase">
                Advisory Services
              </span>
              <h1 className="font-serif text-[36px] md:text-[52px] lg:text-[60px] font-semibold text-text-headline leading-tight mt-4 mb-6">
                Services for Organizations Ready to{" "}
                <span className="italic text-primary">Govern &amp; Grow</span> with AI
              </h1>
              <p className="font-sans text-[17px] md:text-[20px] text-text-muted leading-relaxed max-w-2xl mx-auto">
                Every engagement is tailored. No templates, no cookie-cutter packages — just practical advisory that fits your mission, risk environment, and operational reality.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Services detail */}
      <section className="relative bg-bg py-20 md:py-28 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        <div className="absolute top-1/3 -left-48 w-[400px] h-[400px] bg-primary/3 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 -right-48 w-[400px] h-[400px] bg-primary/3 rounded-full blur-[120px]" />

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-5xl mx-auto flex flex-col gap-16 md:gap-20">
            {services.map((s, i) => (
              <ScrollReveal key={i} delay={i % 2 === 0 ? 0 : 100} direction={i % 2 === 0 ? "left" : "right"}>
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-start">
                  <div className="lg:col-span-2">
                    <div className="w-14 h-14 bg-primary-tint rounded-full flex items-center justify-center mb-5 transition-all duration-300 hover:bg-primary/15 hover:scale-110">
                      <i className={`${s.icon} text-primary text-2xl w-7 h-7 flex items-center justify-center`} />
                    </div>
                    <h2 className="font-serif text-[28px] md:text-[32px] font-semibold text-text-headline mb-2">
                      {s.title}
                    </h2>
                    <p className="font-serif text-[17px] text-primary italic">
                      {s.short}
                    </p>
                  </div>

                  <div className="lg:col-span-3">
                    <p className="font-sans text-[16px] text-text-muted leading-[1.7] mb-4">
                      {s.description}
                    </p>
                    <p className="font-sans text-[16px] text-white leading-relaxed mb-3">
                      {s.leadIn}
                    </p>
                    <ul className="flex flex-col gap-2 mb-6">
                      {s.includes.map((item, j) => (
                        <li
                          key={j}
                          className="font-sans text-[15px] text-text-body flex items-start gap-2"
                        >
                          <i className="ri-check-line text-primary w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <div className="bg-bg-card rounded-xl p-5 border border-border relative overflow-hidden">
                      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-primary/50 to-transparent" />
                      <span className="font-sans text-[14px] font-semibold text-text-headline uppercase tracking-wider pl-4">
                        Ideal For
                      </span>
                      <p className="font-sans text-[15px] text-text-muted mt-2 pl-4">
                        {s.ideal}
                      </p>
                    </div>
                  </div>

                  {i < services.length - 1 && (
                    <div className="lg:col-span-5">
                      <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent w-full" />
                    </div>
                  )}
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-primary py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-white rounded-full blur-3xl" />
        </div>
        <div className="absolute top-10 left-10 w-40 h-40 border border-white/10 rounded-full hidden lg:block" />
        <div className="absolute bottom-10 right-10 w-24 h-24 border border-white/10 rounded-full hidden lg:block" />

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 text-center">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto">
              <h2 className="font-serif text-[32px] md:text-[44px] font-semibold text-white leading-tight mb-5">
                Not Sure Which Service Fits?
              </h2>
              <p className="font-sans text-[17px] md:text-[18px] text-white/80 mb-8">
                That is completely normal. Most organizations start with a conversation, not a service selection. Book a free 30-minute consultation and we will figure out the right path together.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/contact"
                  className="font-sans text-[16px] font-medium text-primary bg-white hover:bg-text-headline px-8 py-4 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105 inline-block"
                >
                  Schedule a Free Consultation
                </Link>
                <a
                  href="mailto:smurray@vantagesightai.com"
                  className="font-sans text-[16px] font-medium text-white border-2 border-white hover:bg-white hover:text-primary px-8 py-4 rounded-full transition-all duration-300 whitespace-nowrap hover:scale-105"
                >
                  Send an Email
                </a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </main>
  );
}